import { TrackingEventKey } from "@next-platform/packages-ts-types";

import generateConversionEventId from "@/features/tracking/generateConversionEventId";
import useFireConversionOnce from "@/features/tracking/useFireConversionOnce";

/**
 * Meta `content_category` — the funnel bucket this view belongs to.
 * Pairs with `TrackingEventKey.PaywallViewed` (used as
 * `content_name`) so media buyers can build "ViewContent WHERE
 * content_category = pricing" retargeting audiences that stay clean
 * if `ViewContent` is later fired on unrelated surfaces (character
 * pages, articles, …).
 *
 * Not hoisted to `@next-platform/packages-ts-types`: `content_category` is
 * Meta-ad-platform-specific enrichment, not part of the canonical
 * event catalog. The existing `EventCategory.Intent` is too broad —
 * `CheckoutStarted` is also `Intent`, so reusing it would pool two
 * distinct audiences.
 */
export const PAYWALL_CONTENT_CATEGORY = "pricing";

/**
 * Fire one `PaywallViewed` conversion per paywall mount.
 *
 * Maps to Meta standard `ViewContent` (see `metaEventMap`) — the
 * industry-standard event for SaaS/subscription pricing pages. The
 * naming-mismatch cost is cosmetic; `ViewContent` wins over a custom
 * event because it:
 * - Gets automatic AEM priority under iOS 14+ ATT.
 * - Plugs into Meta's pre-trained mid-funnel optimization models.
 * - Is retargeting- and lookalike-eligible without a 500-event
 *   warm-up window.
 *
 * Disambiguated from any future unrelated `ViewContent` fires via
 * `content_name` / `content_category` — keeps the paywall audience
 * pool clean.
 *
 * Guarded by a `useRef` latch so StrictMode double-invoke, HMR, and
 * tab re-renders can't fire twice per visit. Caller-agnostic: drop
 * into any component that represents a paywall mount and the event
 * fires once for the lifetime of that mount.
 *
 * Browser-only intent event — no backend CAPI counterpart — so
 * `eventId` is a per-fire uuid with no dedup coupling.
 */
export function usePaywallViewedTracking(): void {
  useFireConversionOnce(() => ({
    eventKey: TrackingEventKey.PaywallViewed,
    eventId: generateConversionEventId(),
    // `content_name` reuses the canonical event key from
    // `common-ts-model` so the Meta param and the backend event
    // catalog can never drift.
    contentName: TrackingEventKey.PaywallViewed,
    contentCategory: PAYWALL_CONTENT_CATEGORY,
  }));
}
